import { create } from 'zustand'

import type { LibrarySearch } from '@/routes/library'

/**
 * Library grid state that is not worth a URL param: the multi-selection
 * (a `Set<uid>`), the roving-tabindex cursor and the last filter used, so
 * that `[`/`]` in the annotator and "back to library" land where the
 * operator left off.
 */

export interface LibraryState {
  selected: Set<string>
  /** Index into the current result list that owns the roving `tabIndex=0`. */
  rovingIndex: number
  /** Uid at the anchor end of a shift-range selection. */
  anchorUid: string | null
  lastSearch: LibrarySearch | null
  /** The uids of the current result page in display order (for range select and prev/next). */
  order: string[]

  setOrder(uids: string[]): void
  setRoving(index: number): void
  select(uid: string, mode?: 'replace' | 'toggle' | 'range'): void
  selectMany(uids: string[]): void
  clearSelection(): void
  setLastSearch(search: LibrarySearch): void
}

export const useLibraryStore = create<LibraryState>()((set, get) => ({
  selected: new Set(),
  rovingIndex: 0,
  anchorUid: null,
  lastSearch: null,
  order: [],

  setOrder: (order) => set({ order }),
  setRoving: (rovingIndex) => set({ rovingIndex: Math.max(0, rovingIndex) }),
  select: (uid, mode = 'replace') => {
    const { selected, anchorUid, order } = get()
    if (mode === 'replace') {
      set({ selected: new Set([uid]), anchorUid: uid })
      return
    }
    if (mode === 'toggle') {
      const next = new Set(selected)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      set({ selected: next, anchorUid: uid })
      return
    }
    // range: everything between the anchor and `uid` in display order
    const a = order.indexOf(anchorUid ?? uid)
    const b = order.indexOf(uid)
    if (a === -1 || b === -1) {
      set({ selected: new Set([...selected, uid]), anchorUid: uid })
      return
    }
    const [lo, hi] = a < b ? [a, b] : [b, a]
    set({ selected: new Set([...selected, ...order.slice(lo, hi + 1)]) })
  },
  selectMany: (uids) => set({ selected: new Set(uids) }),
  clearSelection: () => set({ selected: new Set(), anchorUid: null }),
  setLastSearch: (lastSearch) => set({ lastSearch }),
}))

export const selectSelectedCount = (s: LibraryState): number => s.selected.size
